import { computed, ComputedRef } from 'vue';
import { CANADA, UNITED_STATES } from '../../common/constants';

export interface FormFields {
	validated: boolean;

	fields: {
		[key: string]: {
			value: null | undefined | string | object | boolean | number;
			rawValue?: string;
			validators: string[];
		};
	};

	validators: {
		[key: string]: Function;
	};
}

export interface FormData {
	[key: string]: unknown;
}

export interface Errors {
	[key: string]: string | undefined;
}

export function useFormValidation(form: FormFields) {
	function hasRaw(field: string) {
		return Object.prototype.hasOwnProperty.call(form.fields[field], 'rawValue');
	}

	function getError(field: string): string | undefined {
		const { value, validators } = form.fields[field];

		if (!value && !form.validated) return undefined;

		const error = validators.reduce((previousMessage: string | boolean, validator: string) => {
			if (validator.startsWith('max')) {
				const maxLength = validator.split(':')[1];

				if (value && Number(value.toString().length) > Number(maxLength)) {
					return `Must not be greater than ${maxLength} characters`;
				}
				return previousMessage || false;
			}

			if (validator.startsWith('min')) {
				const minLength = validator.split(':')[1];

				if (value && Number(value.toString().length) > Number(minLength)) {
					return `Must be at least ${minLength} characters`;
				}
				return previousMessage || false;
			}

			if (validator.startsWith('numeric')) {
				// eslint-disable-next-line no-restricted-globals
				if (value && isNaN(Number(value))) {
					return 'Must be a number';
				}
				return previousMessage || false;
			}

			if (validator.startsWith('between')) {
				const [min, max] = validator.split(':')[1].split(',');

				if (value && !Number.isNaN(Number(value)) && (Number(value) < Number(min) || Number(value) > Number(max))) {
					return `Must be between ${min} and ${max}`;
				}
				if (value && Number.isNaN(Number(value)) && (String(value).length > Number(max) || String(value).length < Number(min))) {
					return `Must be between ${min} and ${max} characters`;
				}

				return previousMessage || false;
			}

			const message = form.validators[validator](value);

			return message !== false ? message : previousMessage;
		}, false);

		return error !== false ? error as string : undefined;
	}

	function getData(preferRaw = false): object {
		const data: FormData = {};

		Object.keys(form.fields).forEach(field => {
			data[field] = (hasRaw(field) && preferRaw) ? form.fields[field].rawValue : form.fields[field].value;
		});

		return data;
	}

	function getErrors() {
		const errors: { [key: string]: ComputedRef<string | undefined> } = {};

		Object.keys(form.fields).forEach(field => {
			errors[field] = computed(() => getError(field));
		});

		return errors;
	}

	function formFirstError() {
		let firstError = '';

		Object.entries(form.fields).forEach(field => {
			if (getError(field[0]) !== undefined && firstError === '') {
				[firstError] = field;
			}
		});
		return firstError;
	}

	function validateForm(): boolean {
		form.validated = true; // eslint-disable-line no-param-reassign
		return Object.keys(form.fields).every(field => getError(field) === undefined);
	}

	return {
		getError,
		getData,
		formValid: computed(() => validateForm()),
		errors: computed(() => getErrors()),
		formFirstError: computed(() => formFirstError()),
	};
}

export const validatorRequired = (message = '') => (value: null | boolean | undefined | string | object) => {
	if (typeof value === 'string' && value.length > 0) return false;
	if (typeof value === 'object' && value !== null) return false;
	if (typeof value === 'boolean' && value) return false;
	if (typeof value === 'number' && (value === 0 || value)) return false;

	return message || 'This field is required';
};

export const validatorName = (message = '') => (value: string) => {
	if (value === '' || /^[A-Za-z0-9 -]+$/.test(value)) return false;

	return message || 'Name should include only alphabets (a-z), blank space  and - values';
};

export const validatorPhone = (message = '') => (value: string) => {
	if (value === '' || /^\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/.test(value)) return false;

	return message || 'Phone should be in format xxx-xxx-xxxx';
};

export const validatorEmail = (message = '') => (value: string) => {
	if (value === '' || /^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+\.[a-z.]+$/.test(value)) return false;

	return message || 'Invalid Email';
};

export const validatorAddress = (message = '') => (value: string) => {
	if (value === '' || /^[0-9a-zA-Z ,.'-]+$/.test(value)) return false;

	return message || 'Address should include only alphanumeric (a-z 0-9) and special characters (,.\'-) values';
};

export const validatorZipCode = (message = '', country = '', value = '') => {
	if (country === UNITED_STATES) {
		if (value === '' || /^(\d{5})$/.test(value)) return false;
		return message || 'Zip Code must consist of 5 digits';
	}

	if (country === CANADA) {
		if (value === '' || /^((\w|\d){3}\s(\w|\d){3})$/.test(value)) return false;
		return message || 'Zip Code should be in format xxx xxx';
	}

	if (value === '' || /^((\d{5})|((\w|\d){3}\s(\w|\d){3}))$/.test(value)) return false;

	return message || 'Zip Code must consist of 5 digits';
};

export const validatorSsn = (message = '') => (value: string) => {
	if (value === '' || /^((\d){3}-(\d){2})-(\d){4}$/.test(value)) return false;

	return message || 'SSN number should be 9 digits only in format xxx-xx-xxxx';
};

export const validatorStingLength = (message = '', length = 0) => (value: string) => {
	if (value === '' || value.length === length) return false;

	return message || `Should be ${length} characters long`;
};

export const validatorRequiredWith = (message = '', otherFieldValue: string, value: string) => {
	if (otherFieldValue && !value) {
		return message || 'This field is required';
	}

	return false;
};

export const validatorRequiredIf = (message = '', otherFieldValue: string, inValue: string, value: string) => {
	if (inValue.split(',').indexOf(otherFieldValue) !== -1 && !value) {
		return message || 'This field is required';
	}

	return false;
};

export const validatorBetween = (message = '', range: string, value: string) => {
	const [min, max] = range.split(',');

	if (value === '' || (Number(value) >= Number(min) && Number(value) <= Number(max))) return false;

	return message || `Value should have a size between ${min} and ${max}`;
};

export const validatorRequiredUnless = (message: '', otherFieldValue: string, inValue: string, value: string) => {
	if (otherFieldValue && inValue.split(',').indexOf(otherFieldValue) === -1 && !value) {
		return message || 'This field is required';
	}

	return false;
};

export const validatorDatepicker = (message = '') => (value: string) => {
	if (!/^(\d{1,2}\/\d{1,2}\/\d{4,}|\d{4,}-\d{1,2}-\d{1,2})$/.test(value)) { return false; }

	return message || 'Invalid Date';
};
